import { useState, useEffect, useRef } from "react";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import { apiEndpoints } from "../utils/api";
import MixpanelService, { EventName } from "../mixpanel";

/**
 * Custom hook to create a workspace by posting to the API
 * @param {string} directoryPath - Path to the directory for the workspace
 * @param {string|undefined} projectType - Template ID to use for initialization (can be undefined for empty project)
 * @param {boolean} executeOnMount - Whether to execute the request when the component mounts
 * @param {string} agentType - Type of agent to create
 * @returns {Object} Object containing response data, loading state, error state, and execute function
 */
const useCreateWorkspace = (
  directoryPath,
  projectType,
  executeOnMount = true,
  agentType = "coding_agent",
) => {
  const authenticatedApi = useAuthenticatedApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasInitialized = useRef(false);

  const createWorkspace = async (
    path = directoryPath,
    projectTemplateId = projectType,
    isSandbox = false,
    providedName,
    extras = {},
  ) => {
    if (!path) {
      setError(new Error("Directory path is required"));
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const workspaceData = {
        directory_path: path,
        name: providedName ?? (path.split("/").pop() || directoryPath),
        project_template_id: projectTemplateId, // Send correct field name, allow undefined/null
        is_sandbox: isSandbox,
        ...extras,
      };

      const responseData = await authenticatedApi.post(
        apiEndpoints.workspaces,
        workspaceData,
      );
      setData(responseData);
      setLoading(false);

      // Track project creation event
      MixpanelService.track(EventName.WORKSPACE_CREATED, {
        projectName: responseData.name,
        projectId: responseData.project_id,
        workspaceId: responseData.id,
        templateId: projectTemplateId,
        isSandbox: isSandbox,
      });

      return responseData;
    } catch (err) {
      console.error("Error creating workspace:", err);
      setError(err);
      setLoading(false);
      return null;
    }
  };

  useEffect(() => {
    // Only execute if it hasn't been initialized and executeOnMount is true
    if (
      executeOnMount &&
      directoryPath &&
      projectType &&
      !hasInitialized.current
    ) {
      hasInitialized.current = true;
      createWorkspace(directoryPath, agentType, projectType);
    }
  }, []); // Empty dependency array ensures this only runs once

  return {
    data,
    loading,
    error,
    createWorkspace,
  };
};

export default useCreateWorkspace;
